import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { buildMfAmountItems, computeMfDisplay, formatMfCurrency, getMfItemLabel } from '../../Shared/SavedMfPayslipView';

const MfPayslipSummaryCard = ({
  employee,
  mfPayrollEmployeeData,
  calculatedMfPayslip,
  attendanceAnalytics,
  attendanceFilters,
  holidaysApplied,
  additionalIncome,
  additionalDeductions,
  loanSummary,
  payrollPeriod,
  onOpenPayslip,
  onOpenRecords,
  onOpenCalendar,
  capturedValuesRef
}) => {
  const dailyRate = calculatedMfPayslip?.dailyRate || 0;
  const minuteRate = dailyRate / 8 / 60;
  const absenceDays = attendanceAnalytics?.days_absent || 0;
  const lateMinutes = attendanceAnalytics?.total_minutes_late || 0;
  const undertimeMinutes = attendanceAnalytics?.total_minutes_undertime || 0;
  const mf = parseFloat(mfPayrollEmployeeData?.allowances?.mf) || 0;
  const gas = parseFloat(mfPayrollEmployeeData?.allowances?.gas_allowance) || 0;
  const load = parseFloat(mfPayrollEmployeeData?.allowances?.load_allowance) || 0;
  const housing = parseFloat(mfPayrollEmployeeData?.allowances?.housing_allowance) || 0;
  const mfAmount = mf + gas + load + housing;
  const basicComputed =
    mfAmount - (absenceDays * dailyRate) - (lateMinutes * minuteRate) - (undertimeMinutes * minuteRate);
  const incomeItems = buildMfAmountItems(additionalIncome, dailyRate);
  const deductionItems = buildMfAmountItems(additionalDeductions, dailyRate);
  const absenceDeduction = absenceDays * dailyRate;
  const tardinessDeduction = lateMinutes * minuteRate;
  const undertimeDeduction = undertimeMinutes * minuteRate;
  const additionalIncomeTotal = incomeItems.reduce((s, item) => s + (parseFloat(item?.amount) || 0), 0);
  const grossSalary = basicComputed + additionalIncomeTotal;
  const sssContribution = parseFloat(calculatedMfPayslip?.deductions?.sss) || 0;
  const hdmfContribution = parseFloat(calculatedMfPayslip?.deductions?.hdmf) || 0;
  const philhealthContribution = parseFloat(calculatedMfPayslip?.deductions?.phic) || 0;
  const withholdingTax = parseFloat(calculatedMfPayslip?.deductions?.tax) || 0;
  const additionalDeductionsTotal = deductionItems.reduce((s, item) => s + (parseFloat(item?.amount) || 0), 0);
  const totalGovDeductions = sssContribution + hdmfContribution + philhealthContribution + withholdingTax;
  const totalDeductions = totalGovDeductions + additionalDeductionsTotal;
  const previewRecord = {
    days_absent: absenceDays,
    minutes_late: lateMinutes,
    minutes_undertime: undertimeMinutes,
    absence_deduction: absenceDeduction,
    tardiness_deduction: tardinessDeduction,
    undertime_deduction: undertimeDeduction,
    basic_salary: basicComputed,
    gross_salary: grossSalary,
    additional_income: incomeItems,
    additional_deductions: deductionItems,
    total_deductions: totalDeductions,
    sss_contribution: sssContribution,
    hdmf_contribution: hdmfContribution,
    philhealth_contribution: philhealthContribution,
    withholding_tax: withholdingTax,
    holidays: holidaysApplied,
    tiles: {
      income_breakdown: {
        half_month_salary: mfAmount,
        days_absent: absenceDays,
        minutes_late: lateMinutes,
        minutes_undertime: undertimeMinutes,
        absence: absenceDeduction,
        tardiness: tardinessDeduction,
        undertime: undertimeDeduction,
        gross_salary: grossSalary
      },
      deductions: {
        sss: sssContribution,
        hdmf: hdmfContribution,
        philhealth: philhealthContribution,
        withholding_tax: withholdingTax,
        total_deductions: totalDeductions
      },
      net_pay: {
        gross_salary: grossSalary,
        total_deductions: totalDeductions
      }
    },
    deduction_settings: {
      breakdown: {
        income: {
          half_month_salary: mfAmount,
          items: incomeItems
        },
        deductions: {
          absence: absenceDeduction,
          tardiness: tardinessDeduction,
          undertime: undertimeDeduction,
          sss: sssContribution,
          hdmf: hdmfContribution,
          phic: philhealthContribution,
          tax: withholdingTax,
          items: deductionItems
        },
        summary: {
          basic_salary: basicComputed,
          gross_salary: grossSalary,
          total_deductions: totalDeductions
        }
      }
    }
  };
  const display = computeMfDisplay(previewRecord, loanSummary);
  const hasLoanDetail = display.loanDeduction > 0;
  const netPay = Number.isFinite(display?.netMf) ? display.netMf : Math.max(0, grossSalary - totalDeductions - (display?.loanDeduction || 0));

  const startDate = attendanceFilters?.start_date;
  const endDate = attendanceFilters?.end_date;

  if (capturedValuesRef && typeof capturedValuesRef === 'object') {
    capturedValuesRef.current = {
      isMF: true,
      dailyRate, minuteRate,
      absenceDays, lateMinutes, undertimeMinutes,
      halfMonthSalary: mfAmount,
      absenceDeduction, tardinessDeduction, undertimeDeduction,
      basicComputed, grossSalary,
      sssContribution: display?.mfSss ?? sssContribution,
      hdmfContribution: display?.mfHdmf ?? hdmfContribution,
      philhealthContribution: display?.mfPhilhealth ?? philhealthContribution,
      withholdingTax: display?.mfTax ?? withholdingTax,
      totalGovDeductions,
      totalDeductions: display?.additionalDeductionsTotal ?? totalDeductions,
      additionalIncomeWithAmounts: incomeItems, additionalIncomeTotal,
      additionalDeductionsWithAmounts: deductionItems, additionalDeductionsTotal,
      loanDeduction: display?.loanDeduction || 0,
      hasLoanDetail,
      netPay,
      holidays: Array.isArray(holidaysApplied) ? holidaysApplied : [],
      loanRows: Array.isArray(display?.loanRows) ? display.loanRows : [],
      loanFormTypes: Array.isArray(display?.loanSummary?.loanFormTypes) ? display.loanSummary.loanFormTypes : [],
      loanBreakdown: (display?.loanSummary?.breakdown && typeof display.loanSummary.breakdown === 'object') ? display.loanSummary.breakdown : {},
      displayMfAmount: display?.mfAmount,
      displayAbsenceDeduction: display?.absenceDeduction,
      displayTardinessDeduction: display?.tardinessDeduction,
      displayUndertimeDeduction: display?.undertimeDeduction,
      displayGrossMf: display?.grossMf,
      displayAdditionalDeductionsTotal: display?.additionalDeductionsTotal,
      displayNetMf: display?.netMf
    };
  }

  return (
    <Card className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-indigo-800">MF Payslip Calculation Summary</CardTitle>
        </div>
      </CardHeader>
      <div className="px-4 pb-2 flex flex-wrap gap-2">
        <Button variant="outline" onClick={onOpenPayslip}>MF Payslip</Button>
        <Button variant="outline" onClick={onOpenRecords}>Detailed Attendance Records</Button>
        <Button variant="outline" onClick={onOpenCalendar}>Calendar View</Button>
      </div>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg border border-indigo-200">
            <h3 className="font-bold text-gray-800 mb-2">Employee Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="font-medium">Name:</span> {employee?.full_name}</div>
              <div><span className="font-medium">Branch:</span> {employee?.branch || mfPayrollEmployeeData?.branch || 'N/A'}</div>
              <div><span className="font-medium">Period:</span> {payrollPeriod || 'Not set'}</div>
              <div><span className="font-medium">Dates:</span> {startDate} to {endDate}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-green-800">Income Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {(holidaysApplied || []).length > 0 && (
                  <div className="border-b pb-2 mb-2">
                    <p className="text-xs font-semibold text-gray-700 mb-1">Holidays:</p>
                    <div className="space-y-1">
                      {(holidaysApplied || []).map((h, idx) => (
                        <div key={`${h?.date || 'holiday'}_${idx}`} className="text-xs text-gray-700">
                          {String(h?.date || '')}{h?.name ? ` — ${String(h?.name)}` : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>MF Amount:</span>
                  <span className="font-medium">
                    {formatMfCurrency(display.mfAmount)}
                  </span>
                </div>
                <div className="border-t pt-2">
                  <p className="text-xs font-semibold text-red-700 mb-1">Less:</p>
                  <div className="flex justify-between pl-2 text-xs">
                    <span>Absence ({absenceDays} days):</span>
                    <span className="text-red-600">-{formatMfCurrency(display.absenceDeduction)}</span>
                  </div>
                  <div className="flex justify-between pl-2 text-xs">
                    <span>Tardiness ({lateMinutes} mins):</span>
                    <span className="text-red-600">-{formatMfCurrency(display.tardinessDeduction)}</span>
                  </div>
                  <div className="flex justify-between pl-2 text-xs">
                    <span>Undertime ({undertimeMinutes} mins):</span>
                    <span className="text-red-600">-{formatMfCurrency(display.undertimeDeduction)}</span>
                  </div>
                </div>
                {display.incomeItems.length > 0 && (
                  <div className="border-t pt-2">
                    <p className="text-xs font-semibold text-green-700 mb-1">Additional Income:</p>
                    {display.incomeItems.map((inc, idx) => (
                      <div key={inc.id || `mf-income-${idx}`} className="flex justify-between pl-2 text-xs">
                        <span>{getMfItemLabel(inc)}:</span>
                        <span className="text-green-600">+{formatMfCurrency(inc?.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {display.deductionItems.length > 0 && (
                  <div className="border-t pt-2">
                    <p className="text-xs font-semibold text-red-700 mb-1">Additional Deductions:</p>
                    {display.deductionItems.map((ded, idx) => (
                      <div key={ded.id || `mf-deduction-${idx}`} className="flex justify-between pl-2 text-xs">
                        <span>{getMfItemLabel(ded)}:</span>
                        <span className="text-red-600">-{formatMfCurrency(ded?.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300 border-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-blue-800">Net Pay</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Gross MF:</span>
                    <span className="font-medium">
                      {formatMfCurrency(display.grossMf)}
                    </span>
                  </div>
                  {hasLoanDetail && (
                    <div className="flex justify-between text-sm">
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Total Deductions:</span>
                    <span className="font-medium text-red-600">
                      {formatMfCurrency(display.additionalDeductionsTotal)}
                    </span>
                  </div>
                  {hasLoanDetail && (
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-700">
                        {(Array.isArray(display.loanSummary?.loanFormTypes) ? display.loanSummary.loanFormTypes : []).join(', ') || '—'}
                      </span>
                      <span className="font-medium text-red-600">
                        -{formatMfCurrency(display.loanDeduction)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="border-t-2 border-blue-300 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-blue-900 text-lg">NET MF:</span>
                    <span className="font-bold text-green-700 text-2xl">
                      {formatMfCurrency(display.netMf)}
                    </span>
                  </div>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <p className="text-xs text-blue-700 text-center">
                    ⚠️ MF calculations are independent from Payroll. Set MF employee and period before saving.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MfPayslipSummaryCard;
